import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, ArrowLeft, Image as ImageIcon, Pin, Edit3, Star, StarOff, LogOut, Clock } from "lucide-react";
import { AuthedImage } from "@/components/AuthedImage";
import { GroupAssessmentPanel } from "@/components/GroupAssessmentPanel";

export default function GroupDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const coverRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editingAnn, setEditingAnn] = useState(false);
  const [annDraft, setAnnDraft] = useState("");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [editingTime, setEditingTime] = useState(false);
  const [timeDraft, setTimeDraft] = useState("");

  const load = async () => {
    const [g, p] = await Promise.all([api.get(`/groups/${id}`), api.get(`/groups/${id}/posts`)]);
    setGroup(g.data);
    setPosts(p.data);
  };
  useEffect(() => { load(); }, [id]);

  const post = async () => {
    if (!text.trim()) return;
    await api.post(`/groups/${id}/posts`, { content: text });
    setText("");
    load();
  };

  const copyCode = () => {
    navigator.clipboard.writeText(group.code);
    toast.success("Code copied.");
  };

  const uploadCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)."); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      await api.post(`/groups/${id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover updated.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (coverRef.current) coverRef.current.value = "";
    }
  };

  const saveAnnouncement = async () => {
    const content = annDraft.trim();
    if (!content) { toast.error("Write something first."); return; }
    if (content.length > 800) { toast.error("Announcement too long (max 800 chars)."); return; }
    try {
      await api.put(`/groups/${id}/announcement`, { content });
      toast.success("Pinned.");
      setEditingAnn(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save.");
    }
  };

  const clearAnnouncement = async () => {
    if (!window.confirm("Unpin this announcement?")) return;
    await api.delete(`/groups/${id}/announcement`);
    load();
    toast.success("Unpinned.");
  };

  const promote = async (memberId) => {
    try {
      await api.post(`/groups/${id}/co-leaders/promote`, { user_id: memberId });
      toast.success("Promoted to co-leader.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not promote.");
    }
  };

  const demote = async (memberId) => {
    if (!window.confirm("Remove co-leader access?")) return;
    try {
      await api.post(`/groups/${id}/co-leaders/demote`, { user_id: memberId });
      toast.success("Demoted.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not demote.");
    }
  };

  const performLeave = async () => {
    try {
      const { data } = await api.post(`/groups/${id}/leave`);
      toast.success(data.deleted ? "Group closed." : "You left the group.");
      nav("/app/groups");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not leave.");
    }
  };

  const handoffAndLeave = async () => {
    if (!transferTo) { toast.error("Choose a new leader first."); return; }
    try {
      await api.post(`/groups/${id}/transfer`, { user_id: transferTo });
      await performLeave();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Handoff failed.");
    }
  };

  const handoffOnly = async () => {
    if (!transferTo) { toast.error("Choose a new leader first."); return; }
    try {
      await api.post(`/groups/${id}/transfer`, { user_id: transferTo });
      toast.success("Leadership handed off.");
      setLeaveOpen(false);
      setTransferTo("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Handoff failed.");
    }
  };

  const saveTime = async () => {
    try {
      await api.patch(`/groups/${id}`, { meeting_time: timeDraft.trim() });
      toast.success("Meeting time updated.");
      setEditingTime(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save.");
    }
  };

  if (!group) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <Link to="/app/groups" className="text-sm text-muted-foreground hover:text-foreground" data-testid="back-groups">
        <ArrowLeft className="inline w-4 h-4 mr-1" /> Groups
      </Link>

      <div className="mt-6 relative">
        {group.cover_url ? (
          <AuthedImage url={group.cover_url} alt={`${group.name} cover`}
            className="w-full h-56 md:h-72 object-cover border border-border" />
        ) : (
          <div className="w-full h-56 md:h-72 border border-dashed border-border bg-secondary/30 flex items-center justify-center">
            <div className="text-center text-muted-foreground text-sm">
              <ImageIcon className="w-6 h-6 mx-auto opacity-60" />
              <div className="mt-2 italic">Every group room deserves its own place.</div>
            </div>
          </div>
        )}
        {group.is_leader && (
          <>
            <button onClick={() => coverRef.current?.click()} disabled={uploading}
              data-testid="group-cover-upload"
              className="absolute top-3 right-3 bg-background/90 backdrop-blur px-3 py-1.5 text-xs border border-border hover:border-primary/40">
              {uploading ? "Uploading…" : group.cover_url ? "Change cover" : "Add cover"}
            </button>
            <input ref={coverRef} type="file" accept="image/*" onChange={uploadCover}
              data-testid="group-cover-file" className="hidden" />
          </>
        )}
      </div>

      <header className="mt-8 flex flex-wrap gap-6 justify-between items-end">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary">Led by {group.leader_name}</p>
          <h1 className="font-serif text-5xl mt-2">{group.name}</h1>
          {group.description && <p className="text-muted-foreground mt-3 max-w-xl">{group.description}</p>}
        </div>
        <button onClick={copyCode} data-testid="copy-code"
          className="border border-dashed border-border p-4 hover:border-primary text-left">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Invite code</div>
          <div className="font-mono tracking-widest text-lg mt-1 flex items-center gap-2">
            {group.code} <Copy className="w-3.5 h-3.5 text-muted-foreground" />
          </div>
          <div className="mt-3 pt-3 border-t border-dashed border-border">
            {editingTime ? (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <input value={timeDraft} onChange={(e) => setTimeDraft(e.target.value)}
                  data-testid="meeting-time-input" placeholder="Wednesdays 7pm at Sam's"
                  className="notebook-input text-sm flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveTime(); }
                                       if (e.key === "Escape") setEditingTime(false); }} />
                <button onClick={(e) => { e.stopPropagation(); saveTime(); }}
                  data-testid="meeting-time-save" className="text-xs text-primary underline-serif">Save</button>
              </div>
            ) : group.meeting_time ? (
              <div className="flex items-center gap-2 text-sm" data-testid="meeting-time-display">
                <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="italic">{group.meeting_time}</span>
                {group.is_leader && (
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation();
                                             setTimeDraft(group.meeting_time || ""); setEditingTime(true); }}
                    data-testid="meeting-time-edit" className="ml-auto text-muted-foreground hover:text-foreground">
                    <Edit3 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ) : group.is_leader ? (
              <button onClick={(e) => { e.preventDefault(); e.stopPropagation();
                                         setTimeDraft(""); setEditingTime(true); }}
                data-testid="meeting-time-add"
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> Add meeting time
              </button>
            ) : (
              <div className="text-xs text-muted-foreground italic">No meeting time set yet.</div>
            )}
          </div>
        </button>
      </header>

      <div className="grid md:grid-cols-3 gap-10 mt-12">
        <section className="md:col-span-2">
          {/* Pinned announcement */}
          {(group.announcement || editingAnn) && (
            <div className="border-l-2 border-primary bg-primary/[0.04] px-5 py-4 mb-8" data-testid="group-announcement">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary">
                <Pin className="w-3 h-3" /> Pinned {group.announcement_author && <span className="text-muted-foreground normal-case tracking-normal ml-2">by {group.announcement_author}</span>}
                {group.is_leader && !editingAnn && (
                  <button onClick={() => { setAnnDraft(group.announcement || ""); setEditingAnn(true); }}
                    data-testid="announcement-edit" className="ml-auto text-muted-foreground hover:text-foreground normal-case tracking-normal">
                    <Edit3 className="w-3 h-3" />
                  </button>
                )}
              </div>
              {editingAnn ? (
                <>
                  <Textarea rows={3} value={annDraft} onChange={(e) => setAnnDraft(e.target.value)}
                    data-testid="announcement-input"
                    className="bg-transparent border-0 border-b rounded-none focus-visible:ring-0 mt-2 resize-none" placeholder="Ground rules, meeting time, next steps…" />
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" onClick={saveAnnouncement} data-testid="announcement-save" className="rounded-full">Pin</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingAnn(false)} data-testid="announcement-cancel">Cancel</Button>
                    {group.announcement && (
                      <Button size="sm" variant="ghost" onClick={clearAnnouncement} data-testid="announcement-clear" className="text-destructive ml-auto">Unpin</Button>
                    )}
                  </div>
                </>
              ) : (
                <p className="mt-2 whitespace-pre-wrap leading-relaxed text-[15px]">{group.announcement}</p>
              )}
            </div>
          )}
          {group.is_leader && !group.announcement && !editingAnn && (
            <button onClick={() => { setAnnDraft(""); setEditingAnn(true); }} data-testid="announcement-add"
              className="mb-8 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary flex items-center gap-2">
              <Pin className="w-3 h-3" /> Pin an announcement
            </button>
          )}

          <h2 className="font-serif text-2xl mb-6 flex items-center gap-4">
            <span>Discussion</span><span className="flex-1 rule-line mb-1"></span>
          </h2>
          <div className="border border-border bg-card p-6">
            <Textarea data-testid="post-text" rows={3} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Share a reflection with the group…" className="bg-transparent border-0 focus-visible:ring-0 resize-none" />
            <div className="flex justify-end mt-2">
              <Button onClick={post} data-testid="post-submit" size="sm" className="rounded-full">Post</Button>
            </div>
          </div>
          <ul className="mt-8 space-y-8">
            {posts.map((p) => (
              <li key={p.id} className="border-l border-border pl-5" data-testid={`post-${p.id}`}>
                <div className="text-xs text-muted-foreground">
                  <span className="text-foreground font-medium">{p.author_name}</span> · {new Date(p.created_at).toLocaleString()}
                </div>
                <p className="mt-2 whitespace-pre-wrap leading-relaxed">{p.content}</p>
              </li>
            ))}
            {posts.length === 0 && <li className="text-muted-foreground italic">No posts yet.</li>}
          </ul>
          {group.is_leader && <GroupAssessmentPanel groupId={id} />}
        </section>

        <aside>
          <h2 className="font-serif text-2xl mb-6 flex items-center gap-4">
            <span>Members</span><span className="flex-1 rule-line mb-1"></span>
          </h2>
          <ul className="space-y-3">
            {group.members.map((m) => {
              const isPrimary = m.id === group.leader_id;
              return (
                <li key={m.id} className="flex justify-between items-baseline gap-3" data-testid={`member-${m.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {m.name}
                      {isPrimary && <span className="text-xs text-primary ml-2">Leader</span>}
                      {!isPrimary && m.is_co_leader && <span className="text-xs text-accent ml-2">Co-leader</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{m.weeks_completed} of 11 weeks</div>
                  </div>
                  {group.is_primary_leader && !isPrimary && (
                    m.is_co_leader ? (
                      <button onClick={() => demote(m.id)} data-testid={`demote-${m.id}`}
                        title="Remove co-leader access" className="text-muted-foreground hover:text-destructive">
                        <StarOff className="w-4 h-4" />
                      </button>
                    ) : (
                      <button onClick={() => promote(m.id)} data-testid={`promote-${m.id}`}
                        title="Promote to co-leader" className="text-muted-foreground hover:text-primary">
                        <Star className="w-4 h-4" />
                      </button>
                    )
                  )}
                </li>
              );
            })}
          </ul>

          <div className="mt-10 pt-6 border-t border-border">
            <button onClick={() => setLeaveOpen(true)} data-testid="open-leave-dialog"
              className="text-xs uppercase tracking-widest text-muted-foreground hover:text-destructive flex items-center gap-2">
              <LogOut className="w-3 h-3" /> Leave group
            </button>
          </div>
        </aside>
      </div>

      {/* Leave / Handoff dialog */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent data-testid="leave-dialog" className="max-w-md rounded-none border">
          {(() => {
            const others = group.members.filter((m) => m.id !== group.leader_id);
            const soleMember = group.members.length === 1;
            if (group.is_primary_leader && soleMember) {
              return (
                <>
                  <DialogHeader className="text-left">
                    <DialogTitle className="font-serif text-2xl">Close this group?</DialogTitle>
                    <DialogDescription className="text-muted-foreground mt-2">
                      You're the only member. Leaving will close the group and remove its discussion.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="mt-6 gap-2">
                    <Button variant="ghost" onClick={() => setLeaveOpen(false)} data-testid="leave-cancel">Never mind</Button>
                    <Button onClick={performLeave} data-testid="leave-confirm-delete" className="rounded-full bg-destructive hover:bg-destructive/90">
                      Close group
                    </Button>
                  </DialogFooter>
                </>
              );
            }
            if (group.is_primary_leader) {
              return (
                <>
                  <DialogHeader className="text-left">
                    <div className="text-xs uppercase tracking-[0.2em] text-primary">Hand off leadership</div>
                    <DialogTitle className="font-serif text-2xl mt-2">Choose the next leader.</DialogTitle>
                    <DialogDescription className="text-muted-foreground mt-2">
                      You can hand off and stay on as a co-leader, or hand off and leave the group entirely.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4 max-h-64 overflow-y-auto border border-border">
                    {others.map((m) => (
                      <label key={m.id} className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-border last:border-b-0 ${transferTo === m.id ? "bg-primary/5" : "hover:bg-secondary/40"}`}
                        data-testid={`handoff-option-${m.id}`}>
                        <input type="radio" name="handoff" value={m.id} checked={transferTo === m.id}
                          onChange={() => setTransferTo(m.id)} className="accent-primary" />
                        <div>
                          <div className="font-medium">{m.name}{m.is_co_leader && <span className="text-xs text-accent ml-2">Co-leader</span>}</div>
                          <div className="text-xs text-muted-foreground">{m.weeks_completed} of 11 weeks</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <DialogFooter className="mt-6 gap-2 flex-wrap">
                    <Button variant="ghost" onClick={() => { setLeaveOpen(false); setTransferTo(""); }} data-testid="leave-cancel">
                      Never mind
                    </Button>
                    <Button variant="outline" onClick={handoffOnly} disabled={!transferTo} data-testid="handoff-only" className="rounded-full">
                      Hand off, I'll stay
                    </Button>
                    <Button onClick={handoffAndLeave} disabled={!transferTo} data-testid="handoff-and-leave" className="rounded-full">
                      Hand off and leave
                    </Button>
                  </DialogFooter>
                </>
              );
            }
            // Regular member / co-leader
            return (
              <>
                <DialogHeader className="text-left">
                  <DialogTitle className="font-serif text-2xl">Leave <em>{group.name}</em>?</DialogTitle>
                  <DialogDescription className="text-muted-foreground mt-2">
                    You'll be removed from the roster and won't see new posts. You can always be invited back with the code.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="mt-6 gap-2">
                  <Button variant="ghost" onClick={() => setLeaveOpen(false)} data-testid="leave-cancel">Never mind</Button>
                  <Button onClick={performLeave} data-testid="leave-confirm" className="rounded-full">
                    Yes, leave
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
